import * as React from 'react'

import { Box, type MediumIcon, SearchField } from '@factorialco/deprecated-design-system'
import { debounce } from 'lodash'

import Chip from 'modules/core/components/Filter/base/Chip'
import ChipWithIcon from 'modules/core/components/Filter/base/ChipWithIcon'
import ClearValue from 'modules/core/components/Filter/base/ClearValue'
import Content from 'modules/core/components/Filter/base/Content'
import Provider from 'modules/core/components/Filter/base/Provider'
import useFilter from 'modules/core/components/Filter/base/useFilter'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

type Props = {
  header?: React.ReactNode
  footer?: React.ReactNode
  label: string
  value?: string
  onValueChange: (value?: string) => void
  defaultOpen?: boolean
  icon?: MediumIcon
  noFooter?: boolean
  fullWidth?: boolean
}

const DEBOUNCE_TIME_MS = 700

function Body() {
  const { value, onValueChange } = useFilter<string | undefined>()

  const [search, setSearch] = React.useState(value || '')

  const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearch(event.target.value)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateSearch = useCallback(
    debounce((search: string) => onValueChange(search), DEBOUNCE_TIME_MS),
    []
  )

  useUnsafeEffect(() => {
    if (value === undefined && !search) return
    if (value === search) return
    updateSearch(search)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  return (
    <Box padding="s12" width="s400" maxWidth="full">
      <SearchField
        search={search}
        onSearch={(e) => onChangeSearch(e)}
        variant="big"
        placeholder=""
      />
    </Box>
  )
}

function Search({
  header,
  label,
  value,
  onValueChange,
  defaultOpen,
  footer,
  icon,
  noFooter = false,
  fullWidth,
}: Props) {
  return (
    <Provider value={value} onValueChange={onValueChange} defaultOpen={!!defaultOpen}>
      {!!icon ? (
        <ChipWithIcon label={label} activeLabel={value} icon={icon} />
      ) : (
        <Chip.Toggler active={!!value} label={label} activeLabel={value} fullWidth={fullWidth} />
      )}
      <Content
        header={header}
        body={<Body />}
        footer={!!noFooter ? '' : footer || <ClearValue />}
      />
    </Provider>
  )
}

export default Search
